/**
 * Display-layer formatters. UK locale, GBP, ISO timestamps in / human strings out.
 *
 * Convention: every formatter is total — it never throws. If the input is
 * null/undefined/garbage, return a placeholder ("—" or "").
 */

const PLACEHOLDER = '—';

const dateFmt = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const dateTimeFmt = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
const timeFmt = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
const moneyFmt = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
});

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return PLACEHOLDER;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return PLACEHOLDER;
  return dateFmt.format(d);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return PLACEHOLDER;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return PLACEHOLDER;
  return dateTimeFmt.format(d);
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return PLACEHOLDER;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return PLACEHOLDER;
  return timeFmt.format(d);
}

/** "2h ago" / "in 4 days" — relative to now. */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return PLACEHOLDER;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return PLACEHOLDER;
  const diffMs = d.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const future = diffMs > 0;

  if (abs < 60_000) return future ? 'in <1m' : 'just now';
  if (abs < 3_600_000) {
    const m = Math.round(abs / 60_000);
    return future ? `in ${m}m` : `${m}m ago`;
  }
  if (abs < 86_400_000) {
    const h = Math.round(abs / 3_600_000);
    return future ? `in ${h}h` : `${h}h ago`;
  }
  if (abs < 7 * 86_400_000) {
    const d = Math.round(abs / 86_400_000);
    return future ? `in ${d}d` : `${d}d ago`;
  }
  return formatDate(iso);
}

export function formatMoney(pence: number | null | undefined): string {
  if (pence === null || pence === undefined) return PLACEHOLDER;
  return moneyFmt.format(pence / 100);
}

/** Truncate a ULID to "01HXAB…GNC" for compact display. */
export function shortId(id: string | null | undefined): string {
  if (!id) return PLACEHOLDER;
  if (id.length <= 12) return id;
  return `${id.slice(0, 6)}…${id.slice(-3)}`;
}

/** Metres -> "58.6 km" (>=1km, 1 dp) or "850 m" (<1km). Total; never throws. */
export function formatKm(metres: number | null | undefined): string {
  if (metres === null || metres === undefined || Number.isNaN(metres)) {
    return PLACEHOLDER;
  }
  if (metres < 1000) return `${Math.round(metres)} m`;
  return `${(metres / 1000).toFixed(1)} km`;
}

/** Seconds -> "1h 6m" / "14m" / "45s". Total; never throws. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
    return PLACEHOLDER;
  }
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
